import { newUuid } from '@liniya/api-client';
import { create } from 'zustand';

import { preference } from '../platform';

export type ToastTone = 'info' | 'error';

export interface Toast {
  id: string;
  text: string;
  tone: ToastTone;
}

interface UiState {
  /** Which chat the conversation column is showing. */
  activeChatId: string | null;
  /** Phone layout: whether the conversation covers the list. */
  chatOpen: boolean;
  detailsOpen: boolean;
  searchOpen: boolean;
  toasts: Toast[];
  mediaViewer:
    | { url: string; name: string; kind: 'image' | 'video'; thumbnailUrl?: string | null }
    | null;

  openChat: (chatId: string) => void;
  closeChat: () => void;
  toggleDetails: () => void;
  openDetails: () => void;
  closeDetails: () => void;
  setSearchOpen: (open: boolean) => void;
  pushToast: (text: string, tone?: ToastTone) => void;
  dismissToast: (id: string) => void;
  openMediaViewer: (media: {
    url: string;
    name: string;
    kind: 'image' | 'video';
    /** Already in the browser's cache from the conversation, so it can stand
     * in instantly while the full-size original arrives. */
    thumbnailUrl?: string | null;
  }) => void;
  closeMediaViewer: () => void;
}

const DETAILS_STORAGE_KEY = 'liniya.details-open';

function readDetailsPreference(): boolean {
  return preference().get(DETAILS_STORAGE_KEY) === 'true';
}

function writeDetailsPreference(open: boolean): void {
  preference().set(DETAILS_STORAGE_KEY, String(open));
}

/**
 * Client state only: what is on screen, not what the server knows.
 *
 * Chats, messages and profiles are server state and live in the query cache.
 * Copying them here would create a second source of truth that has to be kept
 * in step with the first, which is how lists start disagreeing with the screen
 * that opened them.
 */
export const useUiStore = create<UiState>((set, get) => ({
  activeChatId: null,
  chatOpen: false,
  detailsOpen: readDetailsPreference(),
  searchOpen: false,
  toasts: [],
  mediaViewer: null,

  openChat: (chatId) => set({ activeChatId: chatId, chatOpen: true }),
  closeChat: () => set({ chatOpen: false, detailsOpen: false }),
  toggleDetails: () => {
    const next = !get().detailsOpen;
    writeDetailsPreference(next);
    set({ detailsOpen: next });
  },
  openDetails: () => {
    writeDetailsPreference(true);
    set({ detailsOpen: true });
  },
  closeDetails: () => {
    writeDetailsPreference(false);
    set({ detailsOpen: false });
  },
  setSearchOpen: (open) => set({ searchOpen: open }),

  pushToast: (text, tone = 'info') => {
    const id = newUuid();
    set((state) => ({ toasts: [...state.toasts, { id, text, tone }] }));
    setTimeout(() => get().dismissToast(id), tone === 'error' ? 5200 : 2800);
  },
  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),

  openMediaViewer: (media) => set({ mediaViewer: media }),
  closeMediaViewer: () => set({ mediaViewer: null }),
}));

/** Show a message to the user from outside a component, such as an event handler. */
export function notify(text: string, tone: ToastTone = 'info'): void {
  useUiStore.getState().pushToast(text, tone);
}
